import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useMemo,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { FieldContext, useFieldContext, useFormApi } from './FormContext.js';
import { getRegisterValidators } from './useFormState.js';
import type { Validator } from './types.js';

export interface FormFieldProps {
  name: string;
  // Validators are heterogeneous in their `value` type (email: string,
  // min: number, required: unknown); widen to accept any specialization.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate?: ReadonlyArray<Validator<any>>;
  /** Label text (rendered as a `<label>`). */
  label?: ReactNode;
  /** Longer description rendered above the control. */
  description?: ReactNode;
  /** Hint text below the control — only shown when there is no error. */
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** A field wrapper — binds a name to the surrounding Form, registers
 *  validators, routes value / error / onChange into the first nested input. */
export function FormField(props: FormFieldProps): ReactNode {
  const { name, validate, label, description, hint, children, className } = props;
  const api = useFormApi();
  const reactId = useId();
  const id = `${reactId}-${name}`;
  const errorId = `${id}-error`;
  const field = api.getField(name);
  const describedBy =
    field.error !== undefined ? errorId : hint !== undefined ? `${id}-hint` : undefined;

  useEffect(() => {
    const register = getRegisterValidators(api);
    register(name, validate);
    return () => {
      register(name, undefined);
    };
  }, [api, name, validate]);

  const ctx = useMemo(() => ({ name, id, errorId, describedBy }), [describedBy, errorId, id, name]);
  const classes = ['vq-field', className].filter(Boolean).join(' ');

  return (
    <FieldContext.Provider value={ctx}>
      <div
        className={classes}
        data-component="FormField"
        data-state={field.error !== undefined ? 'invalid' : field.touched ? 'touched' : 'idle'}
      >
        {label !== undefined ? (
          <label htmlFor={id} className="vq-field__label">
            {label}
          </label>
        ) : null}
        {description !== undefined ? (
          <div className="vq-field__description">{description}</div>
        ) : null}
        <FieldInputBinder>{children}</FieldInputBinder>
        {field.error === undefined && hint !== undefined ? (
          <div id={`${id}-hint`} className="vq-field__hint">
            {hint}
          </div>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

interface FieldInputBinderProps {
  children: ReactNode;
}

/** Clones the FIRST child React element, injecting id / value / onChange /
 *  onBlur / aria-invalid / aria-describedby from the surrounding Form +
 *  FormField. Sibling children (e.g. `<FormFieldError />`) pass through
 *  untouched. Keeps usage boilerplate-free:
 *    `<FormField name="x"><Input /><FormFieldError /></FormField>` works. */
function FieldInputBinder({ children }: FieldInputBinderProps): ReactNode {
  const ctx = useFieldContext();
  const api = useFormApi();
  const field = api.getField(ctx.name);
  const arr = Children.toArray(children);
  let bound = false;
  return arr.map((child, i) => {
    if (bound || !isValidElement(child)) return child;
    bound = true;
    const el = child as ReactElement<HTMLAttributes<HTMLElement> & Record<string, unknown>>;
    const existing = el.props;
    const onChange = (existing.onChange as ((e: unknown) => void) | undefined) ?? undefined;
    const onBlur = (existing.onBlur as ((e: unknown) => void) | undefined) ?? undefined;
    const onValueChange =
      (existing['onValueChange'] as ((v: unknown) => void) | undefined) ?? undefined;
    return cloneElement(el, {
      key: el.key ?? `vq-field-${String(i)}`,
      id: existing.id ?? ctx.id,
      name: ctx.name,
      value: field.value,
      'aria-invalid': field.error !== undefined ? true : undefined,
      'aria-describedby': ctx.describedBy,
      onChange: (e: unknown) => {
        onChange?.(e);
        const target = e as { target?: { value?: unknown; checked?: boolean } } | undefined;
        if (target?.target === undefined) return;
        const nextVal = target.target.value ?? target.target.checked;
        field.setValue(nextVal);
      },
      onValueChange: (v: unknown) => {
        onValueChange?.(v);
        field.setValue(v);
      },
      onBlur: (e: unknown) => {
        onBlur?.(e);
        field.setTouched(true);
      },
    });
  });
}

export interface FormFieldErrorProps {
  className?: string;
}

/** Inline error renderer for the current FormField. Only emits when the
 *  field has been touched and has an error. */
export function FormFieldError(props: FormFieldErrorProps): ReactNode {
  const ctx = useFieldContext();
  const api = useFormApi();
  const field = api.getField(ctx.name);
  if (field.error === undefined || !field.touched) return null;
  const classes = ['vq-field__error', props.className].filter(Boolean).join(' ');
  const message = Array.isArray(field.error) ? field.error.join(', ') : field.error;
  return (
    <div id={ctx.errorId} role="alert" className={classes} data-component="FormFieldError">
      {message}
    </div>
  );
}

export interface FormFieldPendingProps {
  children: ReactNode;
  className?: string;
}

/** Visible while the field has an in-flight async validator. */
export function FormFieldPending(props: FormFieldPendingProps): ReactNode {
  const ctx = useFieldContext();
  const api = useFormApi();
  const field = api.getField(ctx.name);
  if (!field.pending) return null;
  const classes = ['vq-field__pending', props.className].filter(Boolean).join(' ');
  return <div className={classes}>{props.children}</div>;
}
